import React, { useState, useRef, useEffect } from 'react';

const WHERE_OPTIONS = [
  { value: 'northwest', label: 'Pacific Northwest', sub: 'WA · OR · ID · MT', bg: '#e0f2fe', fg: '#0277bd' },
  { value: 'west',      label: 'West Coast',        sub: 'CA · NV · UT',      bg: '#fff3e0', fg: '#e65100' },
  { value: 'southwest', label: 'Southwest',         sub: 'AZ · NM · CO',      bg: '#fce4ec', fg: '#c62828' },
  { value: 'mountain',  label: 'Mountain West',     sub: 'WY · CO · ID',      bg: '#e8f5e9', fg: '#2e7d32' },
  { value: 'midwest',   label: 'Midwest',           sub: 'MN · MI · WI',      bg: '#ede7f6', fg: '#6a1b9a' },
  { value: 'east',      label: 'East Coast',        sub: 'NY · NC · FL',      bg: '#e1f5fe', fg: '#01579b' },
];

const DURATION_OPTIONS = [
  { value: 'weekend',  label: 'Weekend',       sub: '1 – 3 days',  icon: 'ti-alarm-clock' },
  { value: 'week',     label: 'About a week',  sub: '4 – 7 days',  icon: 'ti-calendar' },
  { value: 'extended', label: 'Extended trip', sub: '8+ days',     icon: 'ti-world' },
];

const ACTIVITY_OPTIONS = [
  { value: 'easy',     label: 'Easy & Scenic',  sub: 'Drives, short walks',       icon: 'ti-car' },
  { value: 'moderate', label: 'Moderate',        sub: 'Day hikes, some elevation',  icon: 'ti-map' },
  { value: 'active',   label: 'Very Active',     sub: 'Challenging trails',         icon: 'ti-flag' },
  { value: 'intense',  label: 'Intense',         sub: 'Backpacking, multi-day',     icon: 'ti-stats-up' },
];

function buildUrl(where, duration, activity) {
  const params = new URLSearchParams();
  if (where)    params.set('location', where);
  if (duration) params.set('duration', duration);
  if (activity) params.set('activity', activity);
  const qs = params.toString();
  return `/find-my-trip${qs ? '?' + qs : ''}`;
}

// ── Desktop Airbnb-style pill ──────────────────────────────────────────────
function NavSearchBarAirbnb() {
  const [where, setWhere]       = useState('');
  const [duration, setDuration] = useState('');
  const [activity, setActivity] = useState('');
  const [activeField, setActiveField] = useState(null);
  const wrapperRef = useRef(null);

  useEffect(() => {
    function handleClick(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setActiveField(null);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  function toggleField(field) {
    setActiveField(prev => prev === field ? null : field);
  }

  function pick(field, value) {
    if (field === 'where')    { setWhere(v    => v === value ? '' : value); setActiveField(null); }
    if (field === 'duration') { setDuration(v => v === value ? '' : value); setActiveField(null); }
    if (field === 'activity') { setActivity(v => v === value ? '' : value); setActiveField(null); }
  }

  function handleSearch(e) {
    e.stopPropagation();
    window.location.href = buildUrl(where, duration, activity);
  }

  const whereLabel    = WHERE_OPTIONS.find(o => o.value === where)?.label       || 'Any region';
  const durationLabel = DURATION_OPTIONS.find(o => o.value === duration)?.label || 'Any length';
  const activityLabel = ACTIVITY_OPTIONS.find(o => o.value === activity)?.label || 'Any activity';
  const isOpen = activeField !== null;

  return (
    <div ref={wrapperRef} className="xsearch-airbnb-wrap">
      {/* Pill */}
      <div className={'xsearch-airbnb-pill' + (isOpen ? ' xsearch-airbnb-open' : '')}>
        <button type="button"
          className={'xbtn xsearch-af' + (activeField === 'where' ? ' xsearch-af-active' : '')}
          onClick={() => toggleField('where')}>
          <span className="xsearch-af-label">Where</span>
          <span className={'xsearch-af-value' + (where ? ' xsearch-af-selected' : '')}>{whereLabel}</span>
        </button>

        <span className="xsearch-af-divider" />

        <button type="button"
          className={'xbtn xsearch-af' + (activeField === 'duration' ? ' xsearch-af-active' : '')}
          onClick={() => toggleField('duration')}>
          <span className="xsearch-af-label">Duration</span>
          <span className={'xsearch-af-value' + (duration ? ' xsearch-af-selected' : '')}>{durationLabel}</span>
        </button>

        <span className="xsearch-af-divider" />

        <button type="button"
          className={'xbtn xsearch-af' + (activeField === 'activity' ? ' xsearch-af-active' : '')}
          onClick={() => toggleField('activity')}>
          <span className="xsearch-af-label">Activity</span>
          <span className={'xsearch-af-value' + (activity ? ' xsearch-af-selected' : '')}>{activityLabel}</span>
        </button>

        <button type="button" className="xbtn xsearch-airbnb-go" onClick={handleSearch} aria-label="Find My Trip">
          <i className="ti-search" />
        </button>
      </div>

      {/* WHERE panel — destination card style */}
      {activeField === 'where' && (
        <div className="xsearch-panel xsearch-panel-where">
          <p className="xsearch-panel-colhead">Suggested regions</p>
          <div className="xsearch-dest-grid">
            {WHERE_OPTIONS.map(o => (
              <div key={o.value} role="button" tabIndex={0}
                className={'xsearch-dest-card' + (where === o.value ? ' xsearch-dest-card--sel' : '')}
                onClick={() => pick('where', o.value)}
                onKeyDown={e => e.key === 'Enter' && pick('where', o.value)}>
                <div className="xsearch-dest-icon" style={{ background: o.bg }}>
                  <i className="ti-location-pin" style={{ color: o.fg }} />
                </div>
                <div className="xsearch-dest-text">
                  <span className="xsearch-dest-name">{o.label}</span>
                  <span className="xsearch-dest-sub">{o.sub}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* DURATION panel */}
      {activeField === 'duration' && (
        <div className="xsearch-panel xsearch-panel-duration">
          <p className="xsearch-panel-colhead">How long can you get away?</p>
          <div className="xsearch-option-list">
            {DURATION_OPTIONS.map(o => (
              <div key={o.value} role="button" tabIndex={0}
                className={'xsearch-opt-row' + (duration === o.value ? ' xsearch-opt-row--sel' : '')}
                onClick={() => pick('duration', o.value)}
                onKeyDown={e => e.key === 'Enter' && pick('duration', o.value)}>
                <div className="xsearch-opt-icon"><i className={o.icon} /></div>
                <div>
                  <span className="xsearch-opt-name">{o.label}</span>
                  <span className="xsearch-opt-sub">{o.sub}</span>
                </div>
                {duration === o.value && <i className="ti-check xsearch-opt-check" />}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ACTIVITY panel */}
      {activeField === 'activity' && (
        <div className="xsearch-panel xsearch-panel-activity">
          <p className="xsearch-panel-colhead">How active are you?</p>
          <div className="xsearch-option-list">
            {ACTIVITY_OPTIONS.map(o => (
              <div key={o.value} role="button" tabIndex={0}
                className={'xsearch-opt-row' + (activity === o.value ? ' xsearch-opt-row--sel' : '')}
                onClick={() => pick('activity', o.value)}
                onKeyDown={e => e.key === 'Enter' && pick('activity', o.value)}>
                <div className="xsearch-opt-icon"><i className={o.icon} /></div>
                <div>
                  <span className="xsearch-opt-name">{o.label}</span>
                  <span className="xsearch-opt-sub">{o.sub}</span>
                </div>
                {activity === o.value && <i className="ti-check xsearch-opt-check" />}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Mobile vertical Airbnb-style card (used in drawer) ────────────────────
function NavSearchBarMobile() {
  const [where, setWhere]       = useState('');
  const [duration, setDuration] = useState('');
  const [activity, setActivity] = useState('');
  const [openSection, setOpenSection] = useState('where');

  function toggle(section) {
    setOpenSection(prev => prev === section ? null : section);
  }

  function handleSearch() {
    if (!where && !duration && !activity) {
      window.location.href = '/find-my-trip?start=1';
    } else {
      window.location.href = buildUrl(where, duration, activity);
    }
  }

  function handleClear() {
    setWhere(''); setDuration(''); setActivity(''); setOpenSection('where');
  }

  const whereLabel    = WHERE_OPTIONS.find(o => o.value === where)?.label       || null;
  const durationLabel = DURATION_OPTIONS.find(o => o.value === duration)?.label || null;
  const activityLabel = ACTIVITY_OPTIONS.find(o => o.value === activity)?.label || null;

  return (
    <div className="xmobile-search-card">

      {/* Card heading */}
      <div className="xmobile-card-heading">
        <i className="ti-map-alt" /> Plan your adventure
      </div>

      {/* WHERE section */}
      <div className={'xmobile-section' + (openSection === 'where' ? ' xmobile-section--open' : '')}>
        <div className="xmobile-section-header" role="button" onClick={() => toggle('where')}>
          <span className="xmobile-section-title">Where?</span>
          {whereLabel
            ? <span className="xmobile-section-value xmobile-section-value--set">{whereLabel}</span>
            : <span className="xmobile-section-value">Any region</span>
          }
        </div>
        {openSection === 'where' && (
          <div className="xmobile-section-body">
            {WHERE_OPTIONS.map(o => (
              <div key={o.value} role="button"
                className={'xmobile-dest-row' + (where === o.value ? ' xmobile-dest-row--sel' : '')}
                onClick={() => { setWhere(v => v === o.value ? '' : o.value); setOpenSection('duration'); }}>
                <div className="xmobile-dest-icon" style={{ background: o.bg }}>
                  <i className="ti-location-pin" style={{ color: o.fg }} />
                </div>
                <div className="xmobile-dest-text">
                  <span className="xmobile-dest-name">{o.label}</span>
                  <span className="xmobile-dest-sub">{o.sub}</span>
                </div>
                {where === o.value && <i className="ti-check" style={{ color: '#0a7a8f', marginLeft: 'auto', flexShrink: 0 }} />}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="xmobile-divider" />

      {/* DURATION section */}
      <div className={'xmobile-section' + (openSection === 'duration' ? ' xmobile-section--open' : '')}>
        <div className="xmobile-section-header" role="button" onClick={() => toggle('duration')}>
          <span className="xmobile-section-title">Duration</span>
          {durationLabel
            ? <span className="xmobile-section-value xmobile-section-value--set">{durationLabel}</span>
            : <span className="xmobile-section-value">Add length</span>
          }
        </div>
        {openSection === 'duration' && (
          <div className="xmobile-section-body">
            {DURATION_OPTIONS.map(o => (
              <div key={o.value} role="button"
                className={'xmobile-opt-row' + (duration === o.value ? ' xmobile-opt-row--sel' : '')}
                onClick={() => { setDuration(v => v === o.value ? '' : o.value); setOpenSection('activity'); }}>
                <div className="xmobile-opt-icon"><i className={o.icon} /></div>
                <div>
                  <span className="xmobile-opt-name">{o.label}</span>
                  <span className="xmobile-opt-sub">{o.sub}</span>
                </div>
                {duration === o.value && <i className="ti-check" style={{ color: '#0a7a8f', marginLeft: 'auto', flexShrink: 0 }} />}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="xmobile-divider" />

      {/* ACTIVITY section */}
      <div className={'xmobile-section' + (openSection === 'activity' ? ' xmobile-section--open' : '')}>
        <div className="xmobile-section-header" role="button" onClick={() => toggle('activity')}>
          <span className="xmobile-section-title">Activity</span>
          {activityLabel
            ? <span className="xmobile-section-value xmobile-section-value--set">{activityLabel}</span>
            : <span className="xmobile-section-value">Add activity</span>
          }
        </div>
        {openSection === 'activity' && (
          <div className="xmobile-section-body">
            {ACTIVITY_OPTIONS.map(o => (
              <div key={o.value} role="button"
                className={'xmobile-opt-row' + (activity === o.value ? ' xmobile-opt-row--sel' : '')}
                onClick={() => { setActivity(v => v === o.value ? '' : o.value); setOpenSection(null); }}>
                <div className="xmobile-opt-icon"><i className={o.icon} /></div>
                <div>
                  <span className="xmobile-opt-name">{o.label}</span>
                  <span className="xmobile-opt-sub">{o.sub}</span>
                </div>
                {activity === o.value && <i className="ti-check" style={{ color: '#0a7a8f', marginLeft: 'auto', flexShrink: 0 }} />}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer actions */}
      <div className="xmobile-search-footer">
        <button type="button" className="xbtn xmobile-clear-btn" onClick={handleClear}>Clear all</button>
        <button type="button" className="xbtn xmobile-search-btn" onClick={handleSearch}>
          <i className="ti-search" /> Search
        </button>
      </div>
    </div>
  );
}

function NavSearchBar({ mobile }) {
  if (mobile) return <NavSearchBarMobile />;
  return <NavSearchBarAirbnb />;
}

export default NavSearchBar;
